import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Dialog } from './Dialog';
import { Button } from './Button';
import { CANCEL_REASON_MAX, validateCancelReason } from '@/pages/goodsDeliveryOrders/goodsDeliveryOrderUtils';

interface CancelOrderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Recebe o motivo já aparado. Lançar erro mantém o modal aberto e mostra a mensagem. */
  onConfirm: (reason: string) => Promise<void>;
  title: string;
  /** Ex.: "a ordem de retirada #65". */
  subject: string;
  /** Aviso sobre a rota (padrão: texto da retirada). */
  routeWarning?: string;
}

/**
 * Modal de cancelamento de ordem (retirada/entrega) com motivo obrigatório.
 * Avisa que a parada ainda não executada sai da rota — o backend faz isso na mesma transação.
 */
export const CancelOrderDialog: React.FC<CancelOrderDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  subject,
  routeWarning = 'Se estiver em uma rota ainda não retirada, a parada será removida da rota.',
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setError(null);
      setSaving(false);
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    const validation = validateCancelReason(reason);
    if (validation) {
      setError(validation);
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await onConfirm(reason.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao cancelar a ordem');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      size="md"
      closeOnBackdrop={!saving}
      showCloseButton={!saving}
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            Voltar
          </Button>
          <Button variant="danger" onClick={handleConfirm} loading={saving}>
            Cancelar ordem
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-700">Deseja cancelar {subject}? Esta ação não pode ser desfeita.</p>
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
          <span>{routeWarning}</span>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Motivo do cancelamento <span className="text-red-500">*</span>
          </label>
          <textarea
            className={`input-field min-h-[90px] ${error ? 'border-red-400 focus:ring-red-400' : ''}`}
            value={reason}
            maxLength={CANCEL_REASON_MAX}
            onChange={(e) => { setReason(e.target.value); setError(null); }}
            placeholder="Explique por que a ordem está sendo cancelada"
            disabled={saving}
            autoFocus
          />
          <div className="flex justify-between mt-1">
            {error ? <p className="text-xs text-red-600">{error}</p> : <span />}
            <p className="text-xs text-slate-500">{reason.length}/{CANCEL_REASON_MAX}</p>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
